import { PolicyPage } from './PolicyPage.js';
import type { CurrencyCode } from '../hooks/useCurrency.js';

/**
 * Incident response & breach notification (`docs/landing/incident-response-policy.md`).
 *
 * Counsel-cleared 2026-08-08 and never routed ([B-271](../../../../docs/platform/TODO.md)).
 *
 * ⚠️ **Section 8 ("what is not running yet") is kept in FULL, including the sentence that is
 * hardest to publish:** *"Nothing alerts on anything today … detection depends on someone looking."*
 * An incident-response policy that omits the state of its own detection is the one document where
 * omission is closest to a lie, and the source doc puts it in a table rather than a footnote for
 * exactly that reason.
 *
 * ⚠️ **The timings are commitments and are transcribed exactly** — 30 minutes, 1 hour, 72 hours,
 * 5 business days. Rounding one while "tidying" would change a published promise.
 */
export interface IncidentResponseProps {
  ccyCode: CurrencyCode;
  regionLabel: string;
  onCycleCurrency: () => void;
  onGoHome: () => void;
  onGoDocs: () => void;
  onGoConnectorsDocs: () => void;
  onGoPricing: () => void;
  onOpenAudit: () => void;
  onOpenBooking: () => void;
  onToggleHelp: () => void;
}

const SEVERITY: { sev: string; what: React.ReactNode; example: React.ReactNode }[] = [
  {
    sev: 'SEV-1',
    what: <>Confirmed personal-data breach · cross-customer data exposure · compromise of the store holding your OAuth tokens · a data-destructive execution bug · platform fully down</>,
    example: <>A defect writes to the wrong customer&rsquo;s CRM; a token store is exfiltrated</>,
  },
  {
    sev: 'SEV-2',
    what: <>A vulnerability with plausible exposure, or a single-customer integrity incident</>,
    example: <>A permission check found to be bypassable, no evidence of use; one customer&rsquo;s action batch applied incorrectly</>,
  },
  {
    sev: 'SEV-3',
    what: <>A contained security bug or a control that failed without exposure</>,
    example: <>A guard found not to run in one code path; a monitoring gap</>,
  },
  { sev: 'SEV-4', what: <>A policy deviation</>, example: <>A process step skipped, no technical impact</> },
];

const NOT_RUNNING: { area: string; state: React.ReactNode }[] = [
  {
    area: 'Alerting',
    state: <><strong>Nothing alerts on anything today.</strong> The alert catalogue is specified and the signals it would read are being written to logs and audit trails, but <strong>no alert is wired to a person</strong>. Until it is, detection depends on someone looking.</>,
  },
  { area: 'Status page', state: <>Does not exist.</> },
  { area: 'Uptime monitoring', state: <>Specified and not configured.</> },
  { area: 'On-call rotation', state: <>A founder-sized team, not a rota. The 24×7 SEV-1 language in our operations doc describes the target support model, not today&rsquo;s staffing.</> },
  { area: 'Independent timestamping of the audit chain', state: <>The chain is hash-chained and anchored daily to separate storage; the <strong>external countersigning authority is not contracted</strong>, so tamper-evidence currently rests on our own digest.</> },
  { area: 'Incident history', state: <>None. No customers, no incidents. <strong>We will not construct a track record we do not have.</strong></> },
];

export function IncidentResponse(props: IncidentResponseProps) {
  return (
    <PolicyPage
      section="Trust"
      title="Incident response & breach notification"
      intro="What we commit to when something goes wrong, what you can do without waiting for us, and what is not running yet."
      reviewed="2026-08-08"
      counselCleared
      {...props}
    >
      <h2>The structural advantage, stated first because it changes what an incident means here</h2>
      <p>
        Most SaaS incident policies can promise you notification and a post-mortem. This product can
        promise something more specific, and it is worth understanding before the severity table.
      </p>
      <p>
        <strong>Every write Dedupr makes to your systems is preceded by a snapshot, and every write
        is reversible for 30 days.</strong> That is a product feature, not an incident control — but
        it doubles as one. A rogue, buggy or malicious mass-write inside your CRM is not a
        catastrophe requiring your own backups; it is an action with a pre-image, which we can reverse
        and you can verify.
      </p>
      <ul>
        <li><strong>Containment for a data-integrity incident is a rollback</strong>, not a restore-from-backup exercise with an RPO attached.</li>
        <li><strong>The audit log is hash-chained and append-only</strong>, so forensics after the fact are about reading a record that cannot have been quietly edited — including by us.</li>
      </ul>
      <div className="land-docs__callout">
        <span aria-hidden="true">✻</span>
        <div>
          <strong>What this does not cover:</strong> changes <em>you</em> or your other tools made,
          and changes Dedupr did not make. We snapshot <strong>Dedupr-initiated changes only</strong>.
          Your source systems remain your systems of record and their backup is not our
          responsibility — that is in the DPA in those words.
        </div>
      </div>

      <h2>How we classify</h2>
      <dl className="land-legal__qa">
        {SEVERITY.map((row) => (
          <div key={row.sev}>
            <dt>{row.sev}</dt>
            <dd>
              <p>{row.what}</p>
              <p><em>For example:</em> {row.example}</p>
            </dd>
          </div>
        ))}
      </dl>

      <h2>What we commit to — timings</h2>
      <dl className="land-legal__qa">
        <div>
          <dt>Status page updated</dt>
          <dd>Within <strong>30 minutes</strong> of declaration (SEV-1); within 30 minutes if customer-visible (SEV-2).</dd>
        </div>
        <div>
          <dt>You are contacted directly if your organisation is affected</dt>
          <dd>Within <strong>1 hour</strong> of us identifying that you are affected (SEV-1); within 4 business hours (SEV-2).</dd>
        </div>
        <div>
          <dt>Personal-data breach notification (GDPR Art. 33/34)</dt>
          <dd><strong>Without undue delay and within 72 hours</strong> of us determining a personal data breach has occurred.</dd>
        </div>
        <div>
          <dt>Written post-mortem</dt>
          <dd>Within <strong>5 business days</strong> of resolution (SEV-1); within 10 business days (SEV-2).</dd>
        </div>
        <div>
          <dt>Containment action taken before notification</dt>
          <dd>
            Always. We kill sessions, revoke connection tokens, freeze affected jobs, and — if write
            integrity is in any doubt — <strong>disable the action executor globally</strong> rather
            than per customer.
          </dd>
        </div>
      </dl>

      <h2>What a notification contains</h2>
      <p>Not a reassurance email. Each notification states, to the extent known at the time:</p>
      <ol>
        <li><strong>What happened</strong>, in plain description rather than category language.</li>
        <li><strong>Which of your data was involved</strong> — which objects, roughly how many records, and whether the categories included personal data.</li>
        <li><strong>What we have done</strong> to contain it, and what state your data is in <strong>right now</strong>.</li>
        <li><strong>What you should do</strong>, if anything — including whether to revoke a connection at your end.</li>
        <li><strong>Whether any change to your systems needs reversing</strong>, and whether we have already reversed it.</li>
        <li><strong>What we do not yet know</strong>, named explicitly rather than omitted.</li>
        <li><strong>The reference</strong> you can quote back, and a named person to reply to.</li>
      </ol>
      <div className="land-docs__callout">
        <span aria-hidden="true">✻</span>
        <div>
          <strong>Point 6 is a commitment, not a caveat.</strong> An early notification that hides its
          own uncertainty is how a customer makes a decision on a picture that turns out to be wrong.
        </div>
      </div>

      <h2>What you can do yourself, without waiting for us</h2>
      <p>
        The product is built so that you are not dependent on our response time for the things that
        matter most:
      </p>
      <ul>
        <li><strong>Disconnect any source, immediately.</strong> Disconnecting revokes the token upstream where the provider offers an endpoint and deletes our copy either way — <strong>a failed upstream revocation still deletes our copy and tells you it failed</strong>, because the one action a worried customer takes must never be the one that silently does not happen.</li>
        <li><strong>Roll back any applied action</strong> inside its 30-day window, yourself, without contacting us.</li>
        <li><strong>Read and export your own audit log</strong>, including its integrity verification — you can check the chain rather than take our word that it is intact.</li>
        <li><strong>Revoke any agent credential or API key</strong> immediately; revocation is checked on every call rather than cached, so it takes effect on the next request.</li>
        <li><strong>Close your organisation</strong> and have your data purged, with an export offered first.</li>
      </ul>

      <h2>Our escalation path</h2>
      <p>
        Detection (alerting on authentication anomalies, cross-customer denials, snapshot-prefix
        access, token-refresh failure spikes) → triage and declaration by the on-call engineer, with
        SEV-1 and SEV-2 paging the security lead → containment → eradication and recovery, using the
        snapshot and audit chain for both forensics and your own rollback → notification → a blameless
        written post-mortem with tracked actions.
      </p>
      <p>
        <strong>For a SEV-1 involving customer data, the founder communicates personally.</strong>{' '}
        That is a commitment about who talks to you, not about who does the engineering.
      </p>

      <h2>Reporting something to us</h2>
      <ul>
        <li><strong>security@dedupr.cloud</strong>, and the <code>security.txt</code> published on our domain.</li>
        <li><strong>Acknowledgement within 72 hours</strong>, at MVP.</li>
      </ul>

      <h2>What is not running yet</h2>
      <dl className="land-legal__qa">
        {NOT_RUNNING.map((row) => (
          <div key={row.area}>
            <dt>{row.area}</dt>
            <dd>{row.state}</dd>
          </div>
        ))}
      </dl>
    </PolicyPage>
  );
}
